// components/MobileRecharge.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { apiCall } from "../utils/api";
import { APP_CONFIG } from "../app-config";

const COLORS = APP_CONFIG.colors;

const operators = [
    { id: '100', name: 'Airtel', icon: "https://static.mobikwik.com/appdata/operator_icons/op1.png", color: "#E40000", bgColor: "#E4000015" },
    { id: '101', name: 'Jio', icon: "https://static.mobikwik.com/appdata/operator_icons/op140.png", color: "#0078FF", bgColor: "#0078FF15" },
    { id: '102', name: 'Vi', icon: "https://static.mobikwik.com/appdata/operator_icons/op338.png", color: "#6A0DAD", bgColor: "#6A0DAD15" },
    { id: '103', name: 'BSNL', icon: "https://static.mobikwik.com/appdata/operator_icons/op3.png", color: "#FF6600", bgColor: "#FF660015" },
];

const circles = [
    { id: '1', name: 'Andhra Pradesh' },
    { id: '2', name: 'Assam' },
    { id: '3', name: 'Bihar & Jharkhand' },
    { id: '4', name: 'Chennai' },
    { id: '5', name: 'Delhi & NCR' },
    { id: '6', name: 'Gujarat' },
    { id: '7', name: 'Haryana' },
    { id: '8', name: 'Himachal Pradesh' },
    { id: '9', name: 'Jammu & Kashmir' },
    { id: '10', name: 'Karnataka' },
    { id: '11', name: 'Kerala' },
    { id: '12', name: 'Kolkata' },
    { id: '13', name: 'Maharashtra & Goa (except Mumbai)' },
    { id: '14', name: 'MP & Chhattisgarh' },
    { id: '15', name: 'Mumbai' },
    { id: '16', name: 'North East' },
    { id: '17', name: 'Orissa' },
    { id: '18', name: 'Punjab' },
    { id: '19', name: 'Rajasthan' },
    { id: '20', name: 'Tamilnadu / Telangana' },
    { id: '21', name: 'UP (East)' },
    { id: '22', name: 'UP (West) & Uttarakhand' },
    { id: '23', name: 'West Bengal' },
    { id: '51', name: 'All India (except Delhi/Mumbai)' }
];

function MobileRecharge() {
    const navigate = useNavigate();
    const [mobileForm, setMobileForm] = useState({
        number: '',
        operator_id: '',
        circle_id: '',
    });
    const [operatorData, setOperatorData] = useState(null);
    const [loadingRecentTr, setLoadingRecentTr] = useState(false);
    const [transactionHistorys, setTransactionHistory] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    // Plan Modal states
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [plans, setPlans] = useState({});
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planFetching, setPlanFetching] = useState(false);
    const [planValidationLoading, setPlanValidationLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedPlanId, setExpandedPlanId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editOperatorId, setEditOperatorId] = useState('');
    const [editCircleId, setEditCircleId] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const apiCallRef = useRef(null);

    useEffect(() => {
        const fetchRecentHistory = async () => {
            setLoadingRecentTr(true);
            try {
                const result = await apiCall("recent-history",
                    {
                        category: '2',
                    },
                    true
                );

                if (result.status === true && result.history) {
                    setTransactionHistory(result.history);
                } else {
                    setTransactionHistory([]);
                }
            } catch (error) {
                setTransactionHistory([]);
            } finally {
                setLoadingRecentTr(false);
            }
        };
        fetchRecentHistory();
    }, []);

    // Auto-fetch details when mobile number reaches 10 digits
    useEffect(() => {
        if (apiCallRef.current) {
            clearTimeout(apiCallRef.current);
        }

        if (mobileForm.number && mobileForm.number.length === 10) {
            setIsFetching(true);
            apiCallRef.current = setTimeout(() => {
                fetchMobileDetails();
            }, 500);
        } else {
            setIsFetching(false);
            setOperatorData(null);
        }

        return () => {
            if (apiCallRef.current) {
                clearTimeout(apiCallRef.current);
            }
        };
    }, [mobileForm.number]);

    const fetchMobileDetails = async () => {
        if (!mobileForm.number || mobileForm.number.length !== 10) {
            setIsFetching(false);
            return;
        }

        try {
            const result = await apiCall(
                "mdm-fetch",
                {
                    number: mobileForm.number
                },
                true
            );

            if (result.status === true) {
                const data = result.operator_data;
                setOperatorData(data);
                setMobileForm({
                    ...mobileForm,
                    operator_id: data.operator_id || '',
                    circle_id: data.circle_id || '',
                });
            } else {
                toast.error(result.message || "Invalid mobile number. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setIsFetching(false);
        }
    };

    const handleViewPlans = async () => {
        if (!mobileForm.number || mobileForm.number.length !== 10) {
            toast.error('Please enter a valid 10-digit mobile number');
            return;
        }

        if (!mobileForm.operator_id || !mobileForm.circle_id) {
            toast.error('Please select operator and circle');
            return;
        }

        await fetchPlans(mobileForm.operator_id, mobileForm.circle_id);
    };

    const fetchPlans = async (operatorId, circleId) => {
        if (!operatorId || !circleId) {
            toast.error('Please select both operator and circle');
            return;
        }

        setPlanFetching(true);
        try {
            const result = await apiCall(
                "recharge-plan",
                {
                    operator_id: operatorId,
                    circle_code: circleId
                },
                true
            );

            if (result.status === true) {
                setPlans(result.plans);
                const planTabs = Object.keys(result.plans);
                setTabs(planTabs);
                setActiveTab(planTabs[0]);
                setShowPlanModal(true);
            } else {
                toast.error(result.message || "No plans available for this number.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setPlanFetching(false);
        }
    };

    const handlePlanSelect = async (plan) => {
        setSelectedPlan(plan);
        setPlanValidationLoading(true);

        // Get the selected operator and circle from the form state
        const selectedOp = operators.find(op => op.id === mobileForm.operator_id);
        const selectedCircle = circles.find(c => c.id === mobileForm.circle_id);

        try {
            const result = await apiCall(
                "recharge-validation",
                {
                    operator_id: mobileForm.operator_id,
                    circle_code: mobileForm.circle_id,
                    plan_amount: plan.amount,
                    number: mobileForm.number,
                },
                true
            );

            if (result.status === true) {

                navigate("/payment-confirmation", {
                    state: {
                        service: "Mobile Recharge",
                        operator: selectedOp?.name || '',
                        logo: selectedOp?.icon || '',
                        operator_id: mobileForm.operator_id,
                        number: mobileForm.number,
                        amount: plan.amount,
                        consumerName: 'NA',
                        serviceSpecificData: {
                            circle: selectedCircle?.name || '',
                            circle_id: mobileForm.circle_id,
                            data_pack: plan.Data || 'N/A',
                            validaty: plan.Validity || 'N/A',
                            talktime: plan.Talktime || 'N/A',
                            planDesc: plan.planDescription || ''
                        }
                    }
                });
            } else {
                toast.error(result.message || "Validation failed. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setPlanValidationLoading(false);
        }
    };

    const handleToggleDetails = (planId, e) => {
        e.stopPropagation();
        setExpandedPlanId(expandedPlanId === planId ? null : planId);
    };

    const handleContactClick = () => {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: "GET_CONTACT_INFO"
                })
            );
        } else {
            toast.error("Contact picker not available");
        }
    };

    useEffect(() => {
        const handleContactData = (event) => {
            try {
                let data = event.data;
                if (typeof data === "string") {
                    data = JSON.parse(data);
                }
                if (data?.number) {
                    const cleanNumber = data.number
                        .replace(/\D/g, '')
                        .slice(-10);

                    if (/^\d{10}$/.test(cleanNumber)) {
                        setMobileForm({ ...mobileForm, number: cleanNumber });
                    } else {
                        console.warn("Invalid contact number");
                    }
                }
            } catch (err) {
                console.log("Contact parse error", err);
            }
        };

        window.addEventListener("message", handleContactData);
        return () => {
            window.removeEventListener("message", handleContactData);
        };
    }, []);

    const getFilteredPlans = () => {
        const activePlans = activeTab ? (plans[activeTab] || []) : [];

        if (!searchQuery) {
            return activePlans;
        }

        const isNumeric = !isNaN(searchQuery) && searchQuery !== "";
        const searchAmount = isNumeric ? parseInt(searchQuery) : null;

        const matchedPlans = activePlans.filter(plan =>
            plan.amount?.toString().includes(searchQuery)
        );

        const exactMatch = searchAmount !== null && matchedPlans.some(
            plan => plan.amount === searchAmount
        );

        if (isNumeric && searchAmount > 0 && !exactMatch) {
            const customPlan = {
                amount: searchAmount,
                planDescription: `Custom plan for ₹${searchAmount}`,
                Validity: "N/A",
                Data: "N/A",
                planBenefitItemList: [],
                isCustomPlan: true
            };
            return [customPlan, ...matchedPlans];
        }

        return matchedPlans;
    };

    const filteredPlans = getFilteredPlans();
    const nonIndividualTabs = tabs.filter(tab => tab.toLowerCase() !== 'individual');

    const selectedOperator = operators.find(op => op.id === mobileForm.operator_id) || operators[0];

    const handleSaveChanges = async () => {
        if (!editOperatorId || !editCircleId) {
            toast.error("Please select both operator and circle");
            return;
        }

        setIsSaving(true);
        try {
            const selectedOp = operators.find(op => op.id === editOperatorId);
            const selectedCirc = circles.find(c => c.id === editCircleId);

            if (selectedOp && selectedCirc) {
                setMobileForm({
                    ...mobileForm,
                    operator_id: editOperatorId,
                    circle_id: editCircleId,
                });
                setEditMode(false);
                await fetchPlans(editOperatorId, editCircleId);
                toast.success("Operator and circle updated successfully!");
            }
        } catch (error) {
            toast.error("Failed to update. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                <div
                    className="rounded-2xl p-4 md:p-6"
                    style={{
                        background: COLORS.bgColor,
                        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                        border: `1px solid ${COLORS.borderColor}`,
                    }}
                >
                    <h3 className="text-base md:text-lg font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
                        Mobile Recharge
                    </h3>
                    <form className="space-y-4">
                        {/* Row 1: Mobile Number */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs md:text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                                    Mobile Number
                                </label>
                                <div
                                    className="relative flex items-center rounded-xl transition-all duration-200"
                                    style={{
                                        border: `1px solid ${COLORS.borderColor}`,
                                        background: COLORS.surfaceColor,
                                    }}
                                >
                                    <div className="pl-3 md:pl-4 text-sm md:text-[15px] font-semibold" style={{ color: COLORS.textSecondary }}>
                                        +91
                                    </div>
                                    <input
                                        type="tel"
                                        maxLength={10}
                                        placeholder="Enter 10-digit number"
                                        value={mobileForm.number}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                            setMobileForm({ ...mobileForm, number: val });
                                        }}
                                        className="flex-1 p-3 md:p-3.5 px-2 md:px-3 border-none text-sm md:text-base font-semibold outline-none bg-transparent"
                                        style={{ color: COLORS.textPrimary }}
                                        required
                                    />
                                    <div
                                        className="pr-2 md:pr-3 flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                        onClick={handleContactClick}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="md:w-5 md:h-5">
                                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                            <line x1="20" y1="8" x2="20" y2="14" />
                                            <line x1="23" y1="11" x2="17" y2="11" />
                                        </svg>
                                    </div>
                                </div>
                                {isFetching && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <div
                                            className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin"
                                            style={{
                                                borderColor: `${COLORS.primaryColor}33`,
                                                borderTopColor: COLORS.primaryColor,
                                            }}
                                        />
                                        <span className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>
                                            Fetching operator details...
                                        </span>
                                    </div>
                                )}
                            </div>
                            {mobileForm.number && mobileForm.number.length === 10 && !isFetching && (
                                <div>
                                    <label className="block text-xs md:text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                                        Operator
                                    </label>
                                    <select
                                        value={mobileForm.operator_id}
                                        onChange={(e) => {
                                            const opId = e.target.value;
                                            setMobileForm({ ...mobileForm, operator_id: opId });
                                        }}
                                        className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200 appearance-none text-sm md:text-base"
                                        style={{
                                            background: COLORS.surfaceColor,
                                            borderColor: COLORS.borderColor,
                                            color: COLORS.textPrimary,
                                            '--tw-ring-color': COLORS.primaryColor,
                                        }}
                                    >
                                        <option value="">Select Operator</option>
                                        {operators.map((op) => (
                                            <option key={op.id} value={op.id}>
                                                {op.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Row 2: Circle and View Button */}
                        {mobileForm.number && mobileForm.number.length === 10 && !isFetching && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs md:text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                                        Circle
                                    </label>
                                    <select
                                        value={mobileForm.circle_id}
                                        onChange={(e) => {
                                            setMobileForm({ ...mobileForm, circle_id: e.target.value });
                                        }}
                                        className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200 appearance-none text-sm md:text-base"
                                        style={{
                                            background: COLORS.surfaceColor,
                                            borderColor: COLORS.borderColor,
                                            color: COLORS.textPrimary,
                                            '--tw-ring-color': COLORS.primaryColor,
                                        }}
                                    >
                                        <option value="">Select Circle</option>
                                        {circles.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs md:text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                                        Recharge Plan
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleViewPlans}
                                        className="w-full py-2.5 md:py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-95 text-sm md:text-base"
                                        style={{
                                            background: COLORS.primaryColor,
                                            color: COLORS.textOnPrimary,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        View Plans
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>

                    {/* Recent Recharges */}
                    <div className="mt-6">
                        <h4 className="text-xs md:text-sm font-semibold mb-3" style={{ color: COLORS.textSecondary }}>
                            Recent Recharges
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {loadingRecentTr ? (
                                <div className="space-y-2">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                                            <div className="w-8 h-8 rounded-full" style={{ background: COLORS.borderColor }} />
                                            <div className="flex-1">
                                                <div className="h-3 w-32 rounded" style={{ background: COLORS.borderColor }} />
                                                <div className="h-2 w-24 mt-1 rounded" style={{ background: COLORS.borderColor }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                transactionHistorys
                                    .filter(t => t.order_type === "2")
                                    .slice(0, 3)
                                    .map((txn) => (
                                        <div
                                            key={txn.id}
                                            onClick={() => {
                                                if (txn.cn) {
                                                    setMobileForm({ ...mobileForm, number: txn.cn });
                                                }
                                            }}
                                            className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 active:scale-98 mb-2"
                                            style={{
                                                background: COLORS.surfaceColor,
                                                borderColor: COLORS.borderColor,
                                            }}
                                        >
                                            {txn.logo ? (
                                                <img
                                                    src={txn.logo}
                                                    alt={txn.biller_name}
                                                    className="w-8 h-8 rounded-xl object-contain"
                                                    style={{ background: `${COLORS.primaryColor}15`, padding: '4px' }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="w-8 h-8 rounded-xl items-center justify-center text-sm font-bold"
                                                style={{
                                                    background: `${COLORS.primaryColor}15`,
                                                    color: COLORS.primaryColor,
                                                    display: txn.logo ? 'none' : 'flex'
                                                }}
                                            >
                                                {txn.biller_name ? txn.biller_name.charAt(0) : "R"}
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-xs md:text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                                                    {txn.biller_name || "Unknown"} · {txn.cn || "N/A"}
                                                </div>
                                                <div className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>
                                                    {txn.time || ""}
                                                </div>
                                            </div>
                                            <div className="text-xs md:text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                                ₹{parseFloat(txn.amount).toFixed(2)}
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Plan Modal - Single Modal for both desktop and mobile */}
            {showPlanModal && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-[1100] animate-fadeIn"
                        style={{
                            background: 'rgba(0, 0, 0, 0.5)',
                        }}
                        onClick={() => {
                            if (!planValidationLoading && !isProcessing) {
                                setShowPlanModal(false);
                                setSelectedPlan(null);
                            }
                        }}
                    />

                    {/* Modal Container - Rounded on all sides */}
                    <div className="fixed inset-0 z-[1101] flex items-center justify-center p-4 pointer-events-none">
                        <div
                            className="w-full max-w-4xl h-full md:h-auto max-h-[90vh] rounded-2xl flex flex-col pointer-events-auto animate-slideUp md:animate-fadeIn overflow-hidden"
                            style={{
                                background: COLORS.bgColor,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header with Close Button */}
                            <div className="sticky top-0 pt-3 md:pt-4 pb-2 px-4 md:px-5 border-b flex-shrink-0" style={{ background: COLORS.bgColor, borderColor: COLORS.borderColor }}>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-base md:text-lg font-bold" style={{ color: COLORS.textPrimary }}>Recharge Plans</h3>
                                        <p className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>
                                            {operators.find(op => op.id === mobileForm.operator_id)?.name || ''} · {mobileForm.number}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (!planValidationLoading && !isProcessing) {
                                                setShowPlanModal(false);
                                                setSelectedPlan(null);
                                            }
                                        }}
                                        className="p-1.5 md:p-2 rounded-full hover:bg-black/5 active:scale-90 transition-transform"
                                        style={{ color: COLORS.textSecondary }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="md:w-6 md:h-6">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Operator Info with Edit */}
                                <div className="mt-2 p-2 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                                    {!editMode ? (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-10 h-10 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-bold"
                                                    style={{ background: selectedOperator.bgColor, color: selectedOperator.color }}
                                                >
                                                    <img src={selectedOperator.icon} className="rounded-full" alt="Icon" />
                                                </div>
                                                <div>
                                                    <div className="text-xs md:text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                                        {operators.find(op => op.id === mobileForm.operator_id)?.name || ''}
                                                    </div>
                                                    <div className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>
                                                        {circles.find(c => c.id === mobileForm.circle_id)?.name || ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setEditMode(true);
                                                    setEditOperatorId(mobileForm.operator_id);
                                                    setEditCircleId(mobileForm.circle_id);
                                                }}
                                                className="p-1.5 rounded-lg"
                                                style={{ background: `${COLORS.primaryColor}10` }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="2">
                                                    <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                                                    <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <select
                                                value={editOperatorId}
                                                onChange={(e) => setEditOperatorId(e.target.value)}
                                                className="w-full px-3 py-1.5 rounded-lg text-xs md:text-sm outline-none"
                                                style={{
                                                    background: COLORS.bgColor,
                                                    color: COLORS.textPrimary,
                                                    border: `1px solid ${COLORS.borderColor}`,
                                                }}
                                            >
                                                <option value="">Select Operator</option>
                                                {operators.map((op) => (
                                                    <option key={op.id} value={op.id}>{op.name}</option>
                                                ))}
                                            </select>
                                            <select
                                                value={editCircleId}
                                                onChange={(e) => setEditCircleId(e.target.value)}
                                                className="w-full px-3 py-1.5 rounded-lg text-xs md:text-sm outline-none"
                                                style={{
                                                    background: COLORS.bgColor,
                                                    color: COLORS.textPrimary,
                                                    border: `1px solid ${COLORS.borderColor}`,
                                                }}
                                            >
                                                <option value="">Select Circle</option>
                                                {circles.map((c) => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleSaveChanges}
                                                    disabled={isSaving}
                                                    className="flex-1 py-1.5 rounded-lg text-xs md:text-sm font-semibold"
                                                    style={{ background: COLORS.primaryColor, color: '#fff' }}
                                                >
                                                    {isSaving ? 'Saving...' : 'Save'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditMode(false);
                                                        setEditOperatorId(mobileForm.operator_id);
                                                        setEditCircleId(mobileForm.circle_id);
                                                    }}
                                                    className="px-3 py-1.5 rounded-lg text-xs md:text-sm font-semibold"
                                                    style={{
                                                        background: COLORS.surfaceColor,
                                                        color: COLORS.textSecondary,
                                                        border: `1px solid ${COLORS.borderColor}`,
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Search Bar */}
                                <div className="mt-3">
                                    <div
                                        className="flex items-center gap-2 rounded-xl px-3 md:px-4 py-2 md:py-2.5"
                                        style={{
                                            background: COLORS.surfaceColor,
                                            border: `1px solid ${COLORS.borderColor}`,
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2" className="md:w-[18px] md:h-[18px]">
                                            <circle cx="11" cy="11" r="8" />
                                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                        </svg>
                                        <input
                                            type="number"
                                            placeholder="Search by amount..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="flex-1 bg-transparent border-none outline-none text-xs md:text-sm"
                                            style={{ color: COLORS.textPrimary }}
                                        />
                                        {searchQuery && (
                                            <button onClick={() => setSearchQuery("")} className="p-1 rounded-full active:scale-90 transition-transform">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2.5">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Tabs */}
                                {nonIndividualTabs.length > 0 && (
                                    <div className="mt-3 overflow-x-auto scrollbar-hide pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                        <div className="flex gap-3 md:gap-5 min-w-max">
                                            {nonIndividualTabs.map((cat, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => {
                                                        setActiveTab(cat);
                                                        setSearchQuery('');
                                                    }}
                                                    className="text-xs md:text-sm font-bold whitespace-nowrap pb-2 transition-all"
                                                    style={{
                                                        color: activeTab === cat ? COLORS.primaryColor : COLORS.textSecondary,
                                                        borderBottom: `2px solid ${activeTab === cat ? COLORS.primaryColor : "transparent"}`,
                                                    }}
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Plans List - Scrollable */}
                            <div className="flex-1 overflow-y-auto p-3 md:p-4 pb-24">
                                {planFetching ? (
                                    <div className="text-center py-16">
                                        <div
                                            className="w-10 h-10 md:w-12 md:h-12 rounded-full border-4 border-t-transparent animate-spin mx-auto mb-4"
                                            style={{
                                                borderColor: `${COLORS.primaryColor}33`,
                                                borderTopColor: COLORS.primaryColor,
                                            }}
                                        />
                                        <p className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>Loading plans...</p>
                                    </div>
                                ) : nonIndividualTabs.length === 0 ? (
                                    <div className="text-center py-16">
                                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${COLORS.textSecondary}10` }}>
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="1.5" className="md:w-8 md:h-8">
                                                <circle cx="11" cy="11" r="8" />
                                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                            </svg>
                                        </div>
                                        <p className="text-sm md:text-base font-semibold mb-1" style={{ color: COLORS.textPrimary }}>No plans available</p>
                                        <p className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>Please try again later</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {filteredPlans?.map((plan, idx) => {
                                            const hasBenefits = Array.isArray(plan.planBenefitItemList) && plan.planBenefitItemList.length > 0;
                                            const isExpanded = expandedPlanId === `plan-${idx}`;
                                            const planId = `plan-${idx}`;

                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => !isExpanded && handlePlanSelect(plan)}
                                                    className="p-3 rounded-xl border cursor-pointer transition-all active:scale-[0.98]"
                                                    style={{
                                                        background: isExpanded ? `${COLORS.primaryColor}05` : COLORS.surfaceColor,
                                                        borderColor: isExpanded ? COLORS.primaryColor : COLORS.borderColor,
                                                    }}
                                                >
                                                    <div className="text-base md:text-lg font-bold mb-1.5" style={{ color: COLORS.textPrimary }}>
                                                        ₹{plan.amount}
                                                    </div>

                                                    <div className="flex items-center gap-2 md:gap-3 mb-2 text-xs md:text-sm">
                                                        <div className="flex items-center gap-1">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2">
                                                                <path d="M4 4h16v16H4z" />
                                                                <line x1="8" y1="12" x2="16" y2="12" />
                                                            </svg>
                                                            <span style={{ color: COLORS.textPrimary }}>{plan.Data || "N/A"}</span>
                                                        </div>
                                                        <div className="w-1 h-1 rounded-full" style={{ background: COLORS.textSecondary }} />
                                                        <div className="flex items-center gap-1">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10" />
                                                                <polyline points="12 6 12 12 16 14" />
                                                            </svg>
                                                            <span style={{ color: COLORS.textPrimary }}>{plan.Validity || "N/A"}</span>
                                                        </div>
                                                    </div>

                                                    {hasBenefits && !isExpanded && (
                                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                                            {plan.planBenefitItemList.slice(0, 3).map((item, idx) => (
                                                                item.logoUrl && (
                                                                    <div
                                                                        key={idx}
                                                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md"
                                                                        style={{ background: `${COLORS.textSecondary}08` }}
                                                                    >
                                                                        <img src={item.logoUrl} alt="" className="w-3 h-3 object-contain" />
                                                                        <span className="text-[8px] md:text-[9px]" style={{ color: COLORS.textSecondary }}>
                                                                            {item.displayName.length > 12 ? item.displayName.substring(0, 10) + '..' : item.displayName}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            ))}
                                                            {plan.planBenefitItemList.length > 3 && (
                                                                <div className="px-1.5 py-0.5 rounded-md text-[8px] md:text-[9px]" style={{ background: `${COLORS.textSecondary}08`, color: COLORS.textSecondary }}>
                                                                    +{plan.planBenefitItemList.length - 3}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Full Description - Expanded View */}
                                                    {isExpanded && (
                                                        <div className="mt-3 pt-3 border-t" style={{ borderColor: COLORS.borderColor }}>
                                                            <div className="text-xs md:text-sm text-left" style={{ color: COLORS.textSecondary }}>
                                                                {plan.planDescription?.split('\n').map((line, idx) => (
                                                                    <p key={idx} className="mb-1">{line}</p>
                                                                ))}
                                                            </div>

                                                            {hasBenefits && (
                                                                <div className="mt-3">
                                                                    <div className="text-xs md:text-sm font-semibold mb-2" style={{ color: COLORS.textPrimary }}>Benefits</div>
                                                                    <div className="space-y-1.5">
                                                                        {plan.planBenefitItemList.map((item, idx) => (
                                                                            <div key={idx} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: COLORS.surfaceColor }}>
                                                                                {item.logoUrl && (
                                                                                    <img src={item.logoUrl} alt="" className="w-4 h-4 md:w-5 md:h-5 object-contain" />
                                                                                )}
                                                                                <span className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>{item.displayName}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePlanSelect(plan);
                                                                }}
                                                                className="w-full mt-3 py-2 rounded-xl text-xs md:text-sm font-bold transition-all active:scale-98"
                                                                style={{
                                                                    background: COLORS.primaryColor,
                                                                    color: COLORS.textOnPrimary,
                                                                }}
                                                            >
                                                                Select Plan
                                                            </button>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: COLORS.borderColor }}>
                                                        <span className="text-[10px] md:text-xs flex-1" style={{ color: COLORS.textSecondary }}>
                                                            {isExpanded ? 'Showing full details' : (plan.planDescription?.split('\n')[0]?.substring(0, 40) + '...' || '')}
                                                        </span>
                                                        <button
                                                            onClick={(e) => handleToggleDetails(planId, e)}
                                                            className="text-[10px] md:text-xs font-semibold ml-2 px-2 py-0.5 rounded-md transition-colors"
                                                            style={{ color: COLORS.primaryColor }}
                                                        >
                                                            {isExpanded ? 'Show Less' : 'Details'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {searchQuery && filteredPlans?.length === 0 && (
                                            <div className="text-center py-16">
                                                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${COLORS.textSecondary}10` }}>
                                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="1.5" className="md:w-8 md:h-8">
                                                        <circle cx="11" cy="11" r="8" />
                                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                    </svg>
                                                </div>
                                                <p className="text-sm md:text-base font-semibold mb-1" style={{ color: COLORS.textPrimary }}>No plans found</p>
                                                <p className="text-xs md:text-sm" style={{ color: COLORS.textSecondary }}>Try a different amount</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Plan Validation Loading Overlay - Transparent 30% */}
                    {(planValidationLoading || isProcessing) && (
                        <div
                            className="fixed inset-0 flex items-center justify-center z-[1200]"
                            style={{
                                background: 'rgba(0, 0, 0, 0.3)',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            <div className="flex flex-col items-center gap-3">
                                <div
                                    className="w-10 h-10 md:w-12 md:h-12 rounded-full border-4 border-t-transparent animate-spin"
                                    style={{
                                        borderColor: `${COLORS.primaryColor}33`,
                                        borderTopColor: COLORS.primaryColor,
                                    }}
                                />
                                <div className="text-xs md:text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                                    {planValidationLoading ? 'Validating...' : 'Processing recharge...'}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <style>
                {`
                    .active\\:scale-98:active { transform: scale(0.98); }
                    .active\\:scale-95:active { transform: scale(0.95); }
                    .active\\:scale-90:active { transform: scale(0.9); }
                    .scrollbar-hide::-webkit-scrollbar { display: none; }
                    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                    .animate-fadeIn { animation: fadeIn 0.2s ease forwards; }
                    .animate-slideUp { animation: slideUp 0.3s ease forwards; }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .animate-spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    .animate-pulse {
                        animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                    }
                `}
            </style>
        </>
    );
}

export default MobileRecharge;